import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, EmptyState, IconButton, Icon, Input, Modal, Pill, Select, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { reloadUserGroups } from "../lib/user-groups.js";
import type { UserGroup } from "../lib/user-groups.js";
import { useWorkspace } from "../lib/workspace.js";

/**
 * User groups: a name and an `@handle` that notifies everyone in it — `@design`
 * rather than naming five people one by one.
 *
 * A group belongs to the office it was made in, and holds only that office's
 * people; the server enforces both, and this screen offers only what it would
 * accept. Office admins only — the tab is inside Admin, and every write route
 * checks again.
 */

type OfficeMember = { userId: string; role: string; displayName: string; email: string };

const HANDLE_HINT = "Lowercase letters, digits, dots, dashes or underscores. People type @handle to notify the group.";

function errorText(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function UserGroupsAdmin() {
  const { toast } = useToast();
  const { active } = useWorkspace();
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [people, setPeople] = useState<OfficeMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ groups: UserGroup[] }>("/user-groups?limit=500")
      .then((res) => setGroups(res.groups))
      .catch((error: unknown) => toast(errorText(error, "Could not load groups."), { tone: "danger" }))
      .finally(() => setLoading(false));
    // The mention picker and message chips read the shared copy.
    reloadUserGroups();
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!active) return;
    api
      .get<{ members: OfficeMember[] }>(`/workspaces/${active.id}/members`)
      .then((res) => setPeople(res.members))
      .catch(() => setPeople([]));
  }, [active]);

  const create = () => {
    const trimmedName = name.trim();
    const trimmedHandle = handle.trim().replace(/^@/, "").toLowerCase();
    if (!trimmedName || !trimmedHandle) return;
    setCreating(true);
    api
      .post("/admin/user-groups", { name: trimmedName, handle: trimmedHandle })
      .then(() => {
        setName("");
        setHandle("");
        toast(`@${trimmedHandle} created.`);
        load();
      })
      .catch((error: unknown) => toast(errorText(error, "Could not create that group."), { tone: "danger" }))
      .finally(() => setCreating(false));
  };

  return (
    <section className="admin__panel" aria-label="Groups">
      <form
        className="admin-invite"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <Input label="Group name" placeholder="Design team" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label="Handle"
          placeholder="design"
          hint={HANDLE_HINT}
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          required
        />
        <div className="admin-invite__actions">
          <Button type="submit" loading={creating} disabled={!name.trim() || !handle.trim() || creating}>
            Create group
          </Button>
        </div>
      </form>

      {loading ? (
        <SkeletonText lines={3} />
      ) : groups.length === 0 ? (
        <EmptyState
          icon="people"
          title="No groups yet"
          description="A group lets people notify a whole team with one @handle."
        />
      ) : (
        <div className="admin-offices">
          {groups.map((group) => (
            <GroupCard key={group.id} group={group} people={people} onChanged={load} />
          ))}
        </div>
      )}
    </section>
  );
}

function GroupCard({ group, people, onChanged }: { group: UserGroup; people: OfficeMember[]; onChanged: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [addUserId, setAddUserId] = useState("");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draftName, setDraftName] = useState(group.name);
  const [draftHandle, setDraftHandle] = useState(group.handle);

  const byId = useMemo(() => new Map(people.map((p) => [p.userId, p])), [people]);
  const members = group.memberIds.map((id) => byId.get(id) ?? { userId: id, displayName: "Former member", email: "", role: "" });
  const addable = people.filter((p) => !group.memberIds.includes(p.userId));

  const setMembers = (userIds: string[], done: string) => {
    setBusy(true);
    api
      .put(`/admin/user-groups/${group.id}/members`, { userIds })
      .then(() => {
        toast(done);
        onChanged();
      })
      .catch((error: unknown) => toast(errorText(error, "Could not change the members."), { tone: "danger" }))
      .finally(() => setBusy(false));
  };

  const save = () => {
    setBusy(true);
    api
      .patch(`/admin/user-groups/${group.id}`, {
        name: draftName.trim(),
        handle: draftHandle.trim().replace(/^@/, "").toLowerCase(),
      })
      .then(() => {
        setEditing(false);
        onChanged();
      })
      .catch((error: unknown) => toast(errorText(error, "Could not save the group."), { tone: "danger" }))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    setBusy(true);
    api
      .delete(`/admin/user-groups/${group.id}`)
      .then(() => {
        toast(`@${group.handle} deleted.`);
        setConfirmDelete(false);
        onChanged();
      })
      .catch((error: unknown) => toast(errorText(error, "Could not delete the group."), { tone: "danger" }))
      .finally(() => setBusy(false));
  };

  return (
    <section className="admin-office" aria-label={group.name}>
      <header className="admin-office__head">
        <strong>{group.name}</strong>
        <Pill>@{group.handle}</Pill>
        <span className="admin-office__count">
          {members.length} {members.length === 1 ? "member" : "members"}
        </span>
        <IconButton icon={<Icon name="edit" size={14} />} label={`Edit ${group.name}`} size="sm" onClick={() => setEditing(true)} />
        <IconButton
          icon={<Icon name="trash" size={14} />}
          label={`Delete ${group.name}`}
          size="sm"
          onClick={() => setConfirmDelete(true)}
        />
      </header>

      <div className="admin-office__table" role="table" aria-label={`${group.name} members`}>
        {members.length === 0 ? (
          <div className="admin-office__empty">Nobody in this group yet.</div>
        ) : (
          members.map((m) => (
            <div key={m.userId} role="row" className="admin-office__row">
              <div className="admin-office__person">
                <strong>{m.displayName}</strong>
                <span>{m.email}</span>
              </div>
              <span />
              <div className="admin-office__col-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    setMembers(
                      group.memberIds.filter((id) => id !== m.userId),
                      `${m.displayName} removed from @${group.handle}.`,
                    )
                  }
                >
                  Remove
                </Button>
              </div>
            </div>
          ))
        )}

        <div className="admin-office__row admin-office__row--add" role="row">
          <Select aria-label={`Add someone to ${group.name}`} value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
            <option value="">Add someone…</option>
            {addable.map((p) => (
              <option key={p.userId} value={p.userId}>
                {p.displayName}
              </option>
            ))}
          </Select>
          <span />
          <div className="admin-office__col-actions">
            <Button
              size="sm"
              disabled={!addUserId || busy}
              onClick={() => {
                const person = byId.get(addUserId);
                setMembers([...group.memberIds, addUserId], `${person?.displayName ?? "Person"} added to @${group.handle}.`);
                setAddUserId("");
              }}
            >
              Add
            </Button>
          </div>
        </div>
      </div>

      <Modal open={editing} onClose={() => setEditing(false)} title={`Edit ${group.name}`}>
        <form
          className="admin-invite"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input label="Group name" value={draftName} onChange={(e) => setDraftName(e.target.value)} required />
          <Input label="Handle" hint={HANDLE_HINT} value={draftHandle} onChange={(e) => setDraftHandle(e.target.value)} required />
          <div className="admin-invite__actions">
            <Button type="submit" loading={busy} disabled={!draftName.trim() || !draftHandle.trim() || busy}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title={`Delete @${group.handle}?`}>
        <p>Messages that mentioned it keep their text; it will no longer notify anyone.</p>
        <div className="admin-invite__actions">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={remove}>
            Delete group
          </Button>
        </div>
      </Modal>
    </section>
  );
}
